/**
 * @file Top-down camera rig (GDD §4).
 *
 * Fixed orientation, no automatic rotation. The camera sits on a pivot that
 * smoothly chases the player and leans a configurable amount toward the aim
 * point, which reads as "the character is looking that way" without ever
 * rotating the view.
 */

import * as THREE from 'three';
import { getCameraTuning } from '../config/CameraTuning.js';
import { clamp, damp } from '../utils/MathUtils.js';

export class CameraController {
  /**
   * @param {import('../config/GameConfig.js').GameConfig} config Game config.
   * @param {number} aspect Initial viewport aspect ratio.
   */
  constructor(config, aspect) {
    const settings = getCameraTuning();

    /** @type {THREE.PerspectiveCamera} Starts on a sane 16:9 until told otherwise. */
    this.camera = new THREE.PerspectiveCamera(settings.fov, 16 / 9, settings.near, settings.far);
    this.setAspect(aspect);

    /** @type {number} Current zoom distance. */
    this.distance = settings.distance;

    /** @type {THREE.Vector3} Smoothed look-at point. */
    this.pivot = new THREE.Vector3();

    /** @type {THREE.Vector3} Desired look-at point for this frame. @private */
    this._target = new THREE.Vector3();

    /**
     * Shake energy, 0..1. Displacement scales with trauma squared, which is
     * what makes a small hit barely register while a blast really lands.
     * @type {number}
     * @private
     */
    this._trauma = 0;
    /** @type {number} Seed so two shakes never trace the same path. @private */
    this._shakeSeed = Math.random() * 1000;
    /** @type {number} @private */
    this._shakeTime = 0;
    /** @type {number} @private */
    this._shakeOffsetX = 0;
    /** @type {number} @private */
    this._shakeOffsetZ = 0;
    /** @type {number} @private */
    this._shakeRoll = 0;

    this.camera.position.set(0, this.distance, this.distance);
    this.camera.lookAt(this.pivot);
  }

  /**
   * Applies projection-affecting live camera edits.
   * @returns {void}
   */
  applyTuning() {
    const settings = getCameraTuning();
    this.camera.fov = settings.fov;
    this.camera.near = settings.near;
    this.camera.far = settings.far;
    this.camera.updateProjectionMatrix();
    if (!this._overview) this.distance = clamp(this.distance, settings.minDistance, settings.maxDistance);
    this._applyTransform();
  }

  /**
   * Adds camera shake. Trauma accumulates and is clamped, so a burst of events
   * in one frame cannot throw the camera off the arena.
   *
   * @param {number} amount Trauma to add, 0..1.
   * @returns {void}
   */
  addTrauma(amount) {
    this._trauma = clamp(this._trauma + amount, 0, 1);
  }

  /**
   * Snaps the rig to a position without smoothing. Use on spawn or respawn.
   * @param {THREE.Vector3} position World position to centre on.
   * @returns {void}
   */
  snapTo(position) {
    this.pivot.copy(position);
    this._trauma = 0;
    this._shakeOffsetX = 0;
    this._shakeOffsetZ = 0;
    this._shakeRoll = 0;
    this._applyTransform();
  }

  /**
   * Enters or leaves level-overview mode: the camera stops following the
   * player and frames the whole level instead (the level lab's map view,
   * mirroring the upstream threejs-procedural-dungeon demo).
   *
   * @param {{ minX: number, maxX: number, minZ: number, maxZ: number } | null} bounds
   *   World-space level bounds, or null to resume following the player.
   * @returns {void}
   */
  setOverview(bounds) {
    if (!bounds) {
      if (this._overview) this.distance = getCameraTuning().distance;
      this._overview = null;
      return;
    }
    const cx = (bounds.minX + bounds.maxX) / 2;
    const cz = (bounds.minZ + bounds.maxZ) / 2;
    const halfX = Math.max(4, (bounds.maxX - bounds.minX) / 2);
    const halfZ = Math.max(4, (bounds.maxZ - bounds.minZ) / 2);
    const fov = (this.camera.fov * Math.PI) / 180;
    const fit = Math.max(halfX / (Math.tan(fov / 2) * this.camera.aspect), halfZ / Math.tan(fov / 2));
    this._overview = { x: cx, z: cz, distance: fit * 1.3 + 8 };
  }

  /**
   * Adjusts the zoom distance, clamped to the configured range.
   * @param {number} delta Signed steps; positive zooms out.
   * @returns {void}
   */
  zoom(delta) {
    const settings = getCameraTuning();
    this.distance = clamp(
      this.distance + delta * settings.zoomStep,
      settings.minDistance,
      settings.maxDistance
    );
  }

  /**
   * Follows a subject, optionally leaning toward an aim point.
   * @param {THREE.Vector3} subject Position to follow (the player).
   * @param {THREE.Vector3 | null} aimPoint World-space aim point, or null.
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(subject, aimPoint, dt) {
    const settings = getCameraTuning();

    if (this._overview) {
      // Overview holds the frame on the whole level; damping keeps the
      // transition into and out of it smooth.
      this._target.set(this._overview.x, 0, this._overview.z);
      this.distance = damp(this.distance, this._overview.distance, settings.damping, dt);
      this.pivot.x = damp(this.pivot.x, this._target.x, settings.damping, dt);
      this.pivot.y = damp(this.pivot.y, 0, settings.damping, dt);
      this.pivot.z = damp(this.pivot.z, this._target.z, settings.damping, dt);
      this._updateShake(dt);
      this._applyTransform();
      return;
    }

    this._target.copy(subject);

    if (aimPoint) {
      // Lean a fraction of the way toward the cursor, capped so the player
      // never slides off screen when aiming far away.
      const leadX = (aimPoint.x - subject.x) * settings.aimLead;
      const leadZ = (aimPoint.z - subject.z) * settings.aimLead;
      const leadLength = Math.hypot(leadX, leadZ);
      const scale = leadLength > settings.maxAimLead ? settings.maxAimLead / leadLength : 1;
      this._target.x += leadX * scale;
      this._target.z += leadZ * scale;
    }

    this.pivot.x = damp(this.pivot.x, this._target.x, settings.damping, dt);
    this.pivot.y = damp(this.pivot.y, this._target.y, settings.damping, dt);
    this.pivot.z = damp(this.pivot.z, this._target.z, settings.damping, dt);

    this._updateShake(dt);
    this._applyTransform();
  }

  /**
   * Decays trauma and turns what is left into a camera offset and roll.
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   * @private
   */
  _updateShake(dt) {
    const shake = getCameraTuning().shake;

    if (this._trauma <= 0) {
      this._shakeOffsetX = 0;
      this._shakeOffsetZ = 0;
      this._shakeRoll = 0;
      return;
    }

    this._trauma = Math.max(0, this._trauma - shake.decay * dt);
    this._shakeTime += dt;

    // Squared trauma, and three incommensurable frequencies so the motion never
    // looks like a sine wave.
    const magnitude = this._trauma * this._trauma;
    const t = this._shakeTime * shake.frequency;
    const seed = this._shakeSeed;

    this._shakeOffsetX =
      Math.sin(t + seed) * Math.sin(t * 0.37 + seed) * shake.maxOffset * magnitude;
    this._shakeOffsetZ =
      Math.sin(t * 1.13 + seed * 2) * Math.sin(t * 0.53) * shake.maxOffset * magnitude;
    this._shakeRoll = Math.sin(t * 0.71 + seed * 3) * shake.maxRoll * magnitude;
  }

  /**
   * Places the camera on its orbit given the current pivot, tilt and distance.
   * @returns {void}
   * @private
   */
  _applyTransform() {
    const { tilt, offset } = getCameraTuning();
    const height = Math.sin(tilt) * this.distance;
    const depth = Math.cos(tilt) * this.distance;

    this.camera.position.set(
      this.pivot.x + this._shakeOffsetX + offset.x,
      this.pivot.y + height + offset.y,
      this.pivot.z + depth + this._shakeOffsetZ + offset.z
    );
    this.camera.lookAt(this.pivot);
    // Roll after lookAt, so the shake tilts the frame instead of the aim.
    this.camera.rotateZ(this._shakeRoll);
  }

  /**
   * Refreshes the projection after a viewport resize.
   * @param {number} aspect New aspect ratio.
   * @returns {void}
   */
  setAspect(aspect) {
    // A zero or non-finite aspect makes the projection matrix singular, which
    // NaNs every unprojected ray. Refusing the value keeps the last good one.
    if (!Number.isFinite(aspect) || aspect <= 0) return;
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }
}
